import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { authApi } from '@/api/endpoints/auth';
import { ORGANIZER_BASE } from '@/config/routes';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/Button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { IMG_ORGANIZER, COMPETITION_PAGE_BG } from '@/components/layout/competitionBranding';

const loginSchema = z.object({
  username: z.string().min(2, 'Nazwa użytkownika musi mieć co najmniej 2 znaki'),
  password: z.string().min(6, 'Hasło musi mieć co najmniej 6 znaków'),
});

type LoginFormValues = z.infer<typeof loginSchema>;

export const OrganizerLoginPage = () => {
  const navigate = useNavigate();
  const setAuthenticated = useAuthStore((s) => s.setAuthenticated);

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      username: '',
      password: '',
    },
  });

  const onSubmit = async (values: LoginFormValues) => {
    try {
      const data = await authApi.login(values);
      const me = await authApi.me();
      setAuthenticated(data.username, me.role as 'admin' | 'organizer');
      navigate(`${ORGANIZER_BASE}/leaderboard`, { replace: true });
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Nieprawidłowe dane logowania';
      form.setError('root', { message: msg });
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center px-4" style={{ background: COMPETITION_PAGE_BG }}>
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <img src={IMG_ORGANIZER} alt="Organizator turnieju" className="mx-auto h-12 object-contain" />
          <h1 className="mt-4 text-2xl font-bold tracking-tight text-gray-900">
            Panel Organizatora
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Turniej Przetwórstwo Mleczarskie
          </p>
        </div>

        <div className="rounded-2xl border border-gray-200 bg-white px-8 py-8 shadow-sm">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
              {form.formState.errors.root && (
                <div className="rounded-lg bg-red-50 p-3 text-sm text-red-600">
                  {form.formState.errors.root.message}
                </div>
              )}

              <FormField
                control={form.control}
                name="username"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nazwa użytkownika</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="login"
                        disabled={form.formState.isSubmitting}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Hasło</FormLabel>
                    <FormControl>
                      <Input
                        type="password"
                        placeholder="••••••••"
                        disabled={form.formState.isSubmitting}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button
                type="submit"
                disabled={form.formState.isSubmitting}
                className="w-full"
              >
                Zaloguj się
              </Button>
            </form>
          </Form>
        </div>
      </div>
    </div>
  );
};
